import { ReactNode, useEffect, useRef } from 'react';
import { useGsapContext } from '@/hooks/useGsapContext';
import { ScrollTrigger, gsap } from '@/lib/gsap';

interface StickyProps {
    children: ReactNode;
    wrapperClass?: string;
    className?: string;
    start?: number | string;
    end?: number | string;
    target?: string;
    id?: string;
    enabled?: boolean;
    pinType?: 'fixed' | 'transform';
}

/**
 * Sticky component for pinning elements to the top of the page
 *
 * Note: This is only used for the booking search bar
 * If need to be elsewhere take a look at the matchMedia function
 * As this currently only allows the pinning on desktop for search bar
 * and would need some refactoring if we wanted to use it elsewhere
 */
export function Sticky({ children, wrapperClass, className, start = 0, end = 0, target, id = 'sticky', enabled = true, pinType = 'fixed' }: StickyProps) {
    const pinSpacer = useRef<HTMLDivElement | null>(null);
    const trigger = useRef<HTMLDivElement | null>(null);
    const targetRef = useRef<Element | null>(null);

    useEffect(() => {
        if (target) {
            targetRef.current = document.querySelector(target);
        } else if (pinSpacer.current && pinSpacer.current.parentNode instanceof Element) {
            targetRef.current = pinSpacer.current.parentNode;
        }
    }, [target]);

    useGsapContext(() => {
        if (!enabled || !pinSpacer.current || !trigger.current || !targetRef.current) {
            return;
        }

        gsap.set(trigger.current, { clearProps: 'all' });

        const mm = gsap.matchMedia();

        // Ensure we're only using this for desktop
        mm.add('(min-width: 1080px)', () => {
            // only fire callbacks when the active state toggles
            ScrollTrigger.config({
                limitCallbacks: true,
                ignoreMobileResize: true,
            });

            ScrollTrigger.create({
                id,
                pinType,
                pinSpacing: false,
                pinSpacer: pinSpacer.current,
                trigger: trigger.current,
                scrub: true,
                pin: true,
                start: `top top+=${parseFloat(String(start))}px`,
                end: () => {
                    const targetRefRect = targetRef.current?.getBoundingClientRect();
                    const triggerRect = trigger.current?.getBoundingClientRect();

                    if (targetRefRect && triggerRect) {
                        return `+=${targetRefRect.bottom - triggerRect.bottom + parseFloat(String(end))}`;
                    } else {
                        return '';
                    }
                },
                invalidateOnRefresh: true,
                toggleClass: { targets: trigger.current, className: 'pinned' },
            });
        });
    }, [pinSpacer, trigger, targetRef, start, end, enabled, pinType]);

    useEffect(() => {
        const refreshOnResize = () => {
            if (pinSpacer.current) {
                gsap.set(pinSpacer.current, { clearProps: 'all' });
            }

            ScrollTrigger.refresh();
        };

        window.addEventListener('resize', refreshOnResize);

        return () => {
            window.removeEventListener('resize', refreshOnResize);
        };
    }, []);

    return (
        <div
            ref={(node) => {
                pinSpacer.current = node;
            }}
            className={wrapperClass}
        >
            <div
                ref={(node) => {
                    trigger.current = node;
                }}
                className={className}
            >
                {children}
            </div>
        </div>
    );
}
